import { tabKeyboard } from '@/lib/tabKeyboard';
import { MovableDialog } from '@/components/shared/MovableDialog';
import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  v2,
  type DocumentTemplate,
  type TemplateUsage,
  type TemplateBindingHealth,
  type TemplateVariable,
  type TemplateVersion,
} from '@/api/v2Client';
import { templateVersionLabel } from '@/lib/templateVersion';
import { deleteDialogMessage } from '@/lib/templateDeleteDialog';
import { formatApiError } from '@/lib/errors';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Spinner } from '@/components/dashboard/Spinner';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { PageToast } from '@/components/shared/PageToast';
import { useUiToast } from '@/hooks/useUiToast';
import { formatDate } from '@/lib/format';
import { PermissionDenied } from '@/pages/PermissionDenied';
import { FieldDiffModal } from '@/components/admin/form-templates/FieldDiffModal';
import { VersionDiffPanel } from '@/components/admin/form-templates/VersionDiffPanel';
import { TemplatePreviewModal } from '@/components/admin/form-templates/TemplatePreviewModal';
import { TemplateProvenanceModal } from '@/components/admin/form-templates/TemplateProvenanceModal';
import { StarterProposalReview } from '@/components/admin/form-templates/StarterProposalReview';
import { GovernmentFormsTab } from '@/components/admin/form-templates/GovernmentFormsTab';
import { SubmissionRequirementsTab } from '@/components/admin/form-templates/SubmissionRequirementsTab';
import { useSearchParams } from 'react-router-dom';
import { REGIONS, type Region } from '@/constants/regions';
import { regionOfTemplate } from '@/lib/templateRender';
import { analyzeTemplateVariables, variableWarnings } from '@/lib/templateVariables';

interface FormDraft {
  Name: string;
  Description: string;
  // phase-form-templates-region-sections — '' = region-agnostic (stored null).
  Region: string;
  TargetModule: string;
  Body: string;
  // phase-small-findings-batch-1 (item 1) — the variables are held as a real
  // LIST, edited row by row. They used to be a raw JSON string the author had
  // to hand-author (`VariablesJson`), which is why a stray comma read as
  // "Variables is not valid JSON" with nothing to act on.
  Variables: TemplateVariable[];
}

const EMPTY_DRAFT: FormDraft = {
  Name: '',
  Description: '',
  Region: '',
  TargetModule: '',
  Body: '',
  Variables: [],
};

function templateToDraft(t: DocumentTemplate): FormDraft {
  return {
    Name: t.Name ?? '',
    Description: t.Description ?? '',
    // Normalise the loosely-stored Region column to a canonical REGIONS value
    // so the select shows (and re-saves) it consistently.
    Region: regionOfTemplate(t) ?? '',
    TargetModule: t.TargetModule ?? '',
    Body: t.Body ?? '',
    Variables: (t.Variables ?? []).map((v) => ({ ...v })),
  };
}

// Backend (src/routes/document-templates.ts) emits `{ error, message? }`
// for 400/404/401. ValidationError codes that carry a `message` fall
// through to `parsed.message`; bare codes (NotFound, AuthRequired) get
// a friendlier rewrite via the map below. Phase E-polish-extract
// hoisted the formatXxxError helper to `@/lib/errors`; this map is
// passed in as the `codeMap` argument.
const FORM_TEMPLATE_ERROR_MESSAGES: Record<string, string> = {
  NotFound: 'This template no longer exists. The list may be out of date.',
  AuthRequired: 'Your session expired. Please sign in again.',
};

interface ModalState {
  readonly mode: 'create' | 'edit';
  readonly template: DocumentTemplate | null;
  // phase-form-templates-region-sections — prefill the Region select when
  // "New template" is invoked from inside a single-region section.
  readonly initialRegion?: string;
}

/** Sentinel URL value + section key for region-agnostic (region-less) templates. */
const GLOBAL_REGION_KEY = 'all-regions';

/** Stable, URL/testid-safe slug for a section header/table. */
function regionSlug(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-');
}

// Phase E-polish-extract-3 — inlined ConfirmModal hoisted to
// `@/components/shared/ConfirmModal`.

const tdStyle: CSSProperties = { padding: '12px 8px', verticalAlign: 'top' };

function tabBtn(active: boolean): CSSProperties {
  return {
    padding: '8px 14px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid ' + (active ? 'var(--accent)' : 'transparent'),
    color: active ? 'var(--text)' : 'var(--text-muted)',
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    fontSize: 13,
  };
}

export function FormTemplatesPage(): ReactElement {
  const perms = useUserPermissions();
  // Library management is separate from editing submission documents.
  const canRead = perms.hasPermission('FormTemplates', 'read');
  const canWrite = perms.hasPermission('FormTemplates', 'write');
  const canDelete = perms.hasPermission('FormTemplates', 'delete');
  // T-07 (D-27) — the Government Forms and Submission Requirements tabs call
  // routes gated on AdminForms.* (form-templates-gov-forms.ts,
  // required-templates-catalog.ts, requirements-overrides.ts), so the tabs and
  // their controls follow AdminForms, not Documents. Write and delete do not
  // imply read: a tab is offered only with AdminForms.read.
  const canReadAdminForms = perms.hasPermission('AdminForms', 'read');
  const canWriteAdminForms = perms.hasPermission('AdminForms', 'write');
  const canDeleteAdminForms = perms.hasPermission('AdminForms', 'delete');

  const [templates, setTemplates] = useState<DocumentTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [choosingAdd, setChoosingAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DocumentTemplate | null>(null);
  // Tracker row 10 — who uses the template being deleted, fetched when the
  // dialog opens. null = still checking; 'unknown' = the check failed (said
  // so; the server still enforces the rule).
  const [deleteUsage, setDeleteUsage] = useState<TemplateUsage | 'unknown' | null>(null);
  useEffect(() => {
    if (!deleteTarget) {
      setDeleteUsage(null);
      return;
    }
    let cancelled = false;
    setDeleteUsage(null);
    v2.documentTemplates
      .usage(deleteTarget.Id)
      .then((u) => {
        if (!cancelled) setDeleteUsage(u);
      })
      .catch(() => {
        if (!cancelled) setDeleteUsage('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, [deleteTarget]);
  // Phase 5.X-v1-parity-polish Feature 3 — selected row to diff, or null.
  const [diffSource, setDiffSource] = useState<DocumentTemplate | null>(null);
  // Phase phase-form-templates-ui-fixes (FT-5) — read-only preview target.
  const [previewTarget, setPreviewTarget] = useState<DocumentTemplate | null>(null);
  // phase-template-variable-provenance — the template whose variable sources
  // are open in the provenance modal (opened from the "N variables" cell).
  const [provenanceTarget, setProvenanceTarget] = useState<DocumentTemplate | null>(null);
  // phase-form-template-versioning — version-history modal target + the
  // "Show deleted" toggle (deleted templates are recoverable via Restore).
  const [historyTarget, setHistoryTarget] = useState<DocumentTemplate | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  // phase-form-templates-region-sections — the Region control is now a
  // SECTION selector, not a row filter, and its value lives in the URL
  // (?region=) so a view is shareable and survives refresh. Values:
  //   absent | 'all'          → every non-empty section, stacked
  //   a REGIONS value          → only that region's section
  //   'all-regions' (sentinel) → only the region-agnostic section
  const [searchParams, setSearchParams] = useSearchParams();
  const regionParam = searchParams.get('region');
  const selectedRegion: 'all' | Region | typeof GLOBAL_REGION_KEY =
    regionParam == null || regionParam === 'all'
      ? 'all'
      : regionParam === GLOBAL_REGION_KEY
        ? GLOBAL_REGION_KEY
        : (REGIONS as readonly string[]).includes(regionParam)
          ? (regionParam as Region)
          : 'all';
  const selectRegion = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === 'all') next.delete('region');
          else next.set('region', value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Phase 5.X-form-templates-gov-forms — tab toggle between "Our templates"
  // (the document-templates CRUD that's been here since Phase 5.4) and the
  // new Government Forms catalog.
  const [activeTab, setActiveTab] = useState<'ours' | 'gov' | 'requirements'>('ours');
  // T-07 — a user without Documents.read starts on the first tab they may open.
  useEffect(() => {
    if (!perms.loading && !canRead && canReadAdminForms && activeTab === 'ours') setActiveTab('gov');
  }, [perms.loading, canRead, canReadAdminForms, activeTab]);
  const { toast, showToast, clearToast } = useUiToast();

  useEffect(() => {
    if (!canRead) return;
    setTemplates(null);
    setError(null);
    v2.documentTemplates
      .list(showDeleted ? { includeDeleted: true } : {})
      .then(setTemplates)
      .catch((err: unknown) => {
        setError(formatApiError(err, 'Failed to load templates', FORM_TEMPLATE_ERROR_MESSAGES));
        setTemplates([]);
      });
  }, [canRead, reloadKey, showDeleted]);

  // Form Templates row 6 — per template, bindings whose source no longer
  // exists. Re-read with the list (a binding edit bumps reloadKey via the panel).
  const [bindingAttention, setBindingAttention] = useState<Map<string, TemplateBindingHealth>>(
    () => new Map(),
  );
  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    v2.documentTemplates
      .bindingHealth()
      .then((rows) => {
        if (!cancelled) setBindingAttention(new Map(rows.map((r) => [r.templateId.toLowerCase(), r] as const)));
      })
      .catch(() => {
        // Advisory only: a failed read must not blank the list (and is not "all clear").
        if (!cancelled) setBindingAttention(new Map());
      });
    return (): void => {
      cancelled = true;
    };
  }, [canRead, reloadKey, provenanceTarget]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleDeleteConfirmed = async (t: DocumentTemplate): Promise<void> => {
    setDeleteTarget(null);
    try {
      await v2.documentTemplates.delete(t.Id);
      reload();
      showToast({ type: 'success', text: `Deleted template "${t.Name}". Use "Show deleted" to restore it.` });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to delete template', FORM_TEMPLATE_ERROR_MESSAGES),
      });
    }
  };

  // phase-form-template-versioning — bring a soft-deleted template back.
  const handleUndelete = async (t: DocumentTemplate): Promise<void> => {
    try {
      await v2.documentTemplates.undelete(t.Id);
      reload();
      showToast({ type: 'success', text: `Restored template "${t.Name}".` });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to restore template', FORM_TEMPLATE_ERROR_MESSAGES),
      });
    }
  };

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (!canRead && !canReadAdminForms) return <PermissionDenied />;

  const allTemplates = templates ?? [];

  // One row of a section table (the Region column is dropped — it's implied
  // by the section header — and its width goes to Description).
  const renderRow = (t: DocumentTemplate): ReactElement => {
    const isDeleted = t.DeletedAt != null;
    return (
    <tr
      key={t.Id}
      style={{ borderBottom: '1px solid var(--border)', opacity: isDeleted ? 0.55 : 1 }}
      data-testid={`template-row-${t.Id}`}
    >
      <td style={tdStyle}>
        <div style={{ fontWeight: 600 }}>{t.Name}</div>
        {isDeleted && (
          <span
            title="This template is deleted. Restore brings it back unchanged."
            data-testid={`template-deleted-chip-${t.Id}`}
            style={{
              display: 'inline-block',
              background: '#fee2e2',
              color: '#991b1b',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              marginTop: 4,
              marginLeft: 6,
            }}
          >
            Deleted
          </span>
        )}
      </td>
      {/* phase-form-template-versioning — single-track version (D-059): the
          integer N reads as "N.0". The cell opens the full version history. */}
      <td style={tdStyle}>
        <button
          type="button"
          onClick={() => setHistoryTarget(t)}
          title="View version history (every prior version is retained)"
          data-testid={`template-version-btn-${t.Id}`}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--link, #1B3A6B)',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            cursor: 'pointer',
          }}
        >
          {templateVersionLabel(t.CurrentVersion)}
        </button>
      </td>
      <td
        style={{
          ...tdStyle,
          fontFamily: 'monospace',
          fontSize: 12,
          color: 'var(--text-muted)',
        }}
      >
        {t.TargetModule ?? '—'}
      </td>
      {/* Region column dropped (phase-form-templates-region-sections) — the
          recovered width goes to Description. */}
      <td style={{ ...tdStyle, fontSize: 13, maxWidth: 460 }}>
        {t.Description ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
      </td>
      <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text-muted)' }}>
        {/* phase-template-variable-provenance — the count opens a per-template
            provenance view (where each variable's value comes from). */}
        <button
          type="button"
          onClick={() => setProvenanceTarget(t)}
          title="See where each variable's value comes from at generation time"
          data-testid={`template-provenance-btn-${t.Id}`}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            color: 'var(--link, #1B3A6B)',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            cursor: 'pointer',
          }}
        >
          {t.Variables.length} variable{t.Variables.length === 1 ? '' : 's'}
        </button>
        {/* Form Templates row 6 — bindings whose source no longer exists. */}
        {(bindingAttention.get(t.Id.toLowerCase())?.needsAttention ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => setProvenanceTarget(t)}
            data-testid={`template-bindings-attention-${t.Id}`}
            title={bindingAttention
              .get(t.Id.toLowerCase())!
              .broken.map((b) => `«${b.variableName}»: ${b.reason}`)
              .join('\n')}
            style={{
              display: 'block',
              marginTop: 3,
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--red, #b91c1c)',
              cursor: 'pointer',
            }}
          >
            ⚠ {bindingAttention.get(t.Id.toLowerCase())!.needsAttention}{' '}
            {bindingAttention.get(t.Id.toLowerCase())!.needsAttention === 1
              ? 'binding needs attention'
              : 'bindings need attention'}
          </button>
        )}
      </td>
      <td style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}>
        {formatDate(t.UpdatedAt)}
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
        <button
          type="button"
          onClick={() => setPreviewTarget(t)}
          title="Preview the rendered template (read-only)"
          data-testid={`template-preview-btn-${t.Id}`}
          style={{
            background: 'none',
            color: 'var(--text-muted)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '4px 10px',
            marginRight: 8,
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          Preview
        </button>
        {(() => {
          // Compare is across ALL templates, not just this section.
          const hasPeer = allTemplates.some((other) => other.Id !== t.Id);
          return (
            <button
              type="button"
              onClick={() => setDiffSource(t)}
              disabled={!hasPeer}
              title={
                hasPeer
                  ? 'Compare this template against another'
                  : 'Need at least 2 templates to compare'
              }
              data-testid={`template-compare-btn-${t.Id}`}
              style={{
                background: 'none',
                color: 'var(--text-muted)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '4px 10px',
                marginRight: 8,
                cursor: hasPeer ? 'pointer' : 'not-allowed',
                opacity: hasPeer ? 1 : 0.5,
                fontSize: 12,
              }}
            >
              Compare
            </button>
          );
        })()}
        {canWrite && !isDeleted && (
          <button
            type="button"
            onClick={() => setModal({ mode: 'edit', template: t })}
            style={{
              background: 'none',
              color: 'var(--accent)',
              border: '1px solid var(--accent)',
              borderRadius: 4,
              padding: '4px 10px',
              marginRight: 8,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Edit
          </button>
        )}
        {canDelete && !isDeleted && (
          <button
            type="button"
            onClick={() => setDeleteTarget(t)}
            data-testid={`template-delete-btn-${t.Id}`}
            style={{
              background: 'none',
              color: 'var(--red)',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Delete
          </button>
        )}
        {canDelete && isDeleted && (
          <button
            type="button"
            onClick={() => void handleUndelete(t)}
            data-testid={`template-restore-btn-${t.Id}`}
            style={{
              background: 'none',
              color: 'var(--accent)',
              border: '1px solid var(--accent)',
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Restore
          </button>
        )}
      </td>
    </tr>
    );
  };

  // A per-region section: header ("FDA — 4 templates") + its own table.
  const renderSection = (
    key: string,
    label: string,
    sectionTemplates: DocumentTemplate[],
  ): ReactElement => (
    <div key={key} data-testid={`template-section-${key}`} style={{ marginBottom: 28 }}>
      <h2
        data-testid={`template-section-header-${key}`}
        style={{ fontSize: 15, margin: '0 0 8px 0', display: 'flex', alignItems: 'baseline', gap: 8 }}
      >
        {label}
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>
          {sectionTemplates.length} template{sectionTemplates.length === 1 ? '' : 's'}
        </span>
      </h2>
      <table
        data-testid={`template-section-table-${key}`}
        style={{ width: '100%', borderCollapse: 'collapse' }}
      >
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
            <th style={tdStyle}>Name</th>
            {/* phase-form-template-versioning — the template's own single-track
                version (1.0, 2.0, …). Distinct from the eCTD Section column:
                the old "Module" heading sat over values like 1.6.3, which are
                eCTD SECTION codes, not versions — two numbering schemes were
                colliding on one screen. */}
            <th style={tdStyle}>Version</th>
            <th style={tdStyle} title="The eCTD section this template's output files under (e.g. m1.2, 1.6.3)">
              eCTD Section
            </th>
            <th style={tdStyle}>Description</th>
            <th style={tdStyle}>Variables</th>
            <th style={tdStyle}>Updated</th>
            <th style={tdStyle}></th>
          </tr>
        </thead>
        <tbody>{sectionTemplates.map(renderRow)}</tbody>
      </table>
    </div>
  );

  // Honest empty state for a directly-selected region with no templates.
  const renderRegionEmpty = (label: string, prefillRegion?: string): ReactElement => (
    <div
      data-testid="form-templates-region-empty"
      style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}
    >
      <div style={{ fontSize: 32, marginBottom: 8 }} aria-hidden="true">
        📋
      </div>
      <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        No {label} templates yet
      </div>
      {canWrite && (
        <button
          type="button"
          onClick={() => setModal({ mode: 'create', template: null, initialRegion: prefillRegion })}
          style={{
            marginTop: 12,
            padding: '6px 12px',
            border: '1px solid var(--accent)',
            borderRadius: 6,
            background: 'var(--accent)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + New template
        </button>
      )}
    </div>
  );

  // Group templates by canonical region; region-less → the global bucket.
  const groups = new Map<Region, DocumentTemplate[]>(REGIONS.map((r) => [r, [] as DocumentTemplate[]]));
  const globalGroup: DocumentTemplate[] = [];
  for (const t of allTemplates) {
    const r = regionOfTemplate(t);
    if (r) groups.get(r)!.push(t);
    else globalGroup.push(t);
  }

  const renderOurTemplates = (): ReactElement => {
    if (allTemplates.length === 0) {
      // Nothing at all — the original "create your first template" state.
      return (
        <div
          data-testid="form-templates-empty-row"
          style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}
        >
          <div style={{ fontSize: 32, marginBottom: 8 }} aria-hidden="true">
            📋
          </div>
          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
            No form templates yet
          </div>
          <div>
            Templates standardise document generation across submissions.
            {canWrite && ' Create one to get started.'}
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={() => setModal({ mode: 'create', template: null })}
              style={{
                marginTop: 12,
                padding: '6px 12px',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                background: 'var(--accent)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Create first template
            </button>
          )}
        </div>
      );
    }

    if (selectedRegion === GLOBAL_REGION_KEY) {
      return globalGroup.length > 0
        ? renderSection(GLOBAL_REGION_KEY, 'All regions', globalGroup)
        : renderRegionEmpty('region-agnostic');
    }

    if (selectedRegion !== 'all') {
      const list = groups.get(selectedRegion) ?? [];
      return list.length > 0
        ? renderSection(regionSlug(selectedRegion), selectedRegion, list)
        : renderRegionEmpty(selectedRegion, selectedRegion);
    }

    // 'all' — every NON-EMPTY region section in canonical order, then the
    // region-agnostic section last (if any).
    const sections: ReactElement[] = [];
    for (const r of REGIONS) {
      const list = groups.get(r) ?? [];
      if (list.length > 0) sections.push(renderSection(regionSlug(r), r, list));
    }
    if (globalGroup.length > 0) {
      sections.push(renderSection(GLOBAL_REGION_KEY, 'All regions', globalGroup));
    }
    return <div data-testid="form-templates-sections">{sections}</div>;
  };

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Forms and templates</h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
            Manage your tenant’s reusable templates and official forms. Each submission gets its own copy; editing a submission form does not change this library.
          </p>
        </div>
        {((canRead && canWrite) || (canReadAdminForms && canWriteAdminForms)) && (
          <button type="button" onClick={() => setChoosingAdd(true)}
            style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
            Add form or template
          </button>
        )}
      </div>

      {choosingAdd && (
        <MovableDialog persistKey="formTemplates.add" title="Add form or template" ariaLabel="Add form or template" onClose={() => setChoosingAdd(false)} width="min(560px, 92vw)">
          <p style={{ margin: '0 0 16px' }}>Choose the source. Everything saved here belongs to your tenant.</p>
          {canRead && canWrite && <div style={{ marginBottom: 16 }}>
            <button type="button" style={{ width: '100%', textAlign: 'left', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontWeight: 600, cursor: 'pointer' }} onClick={() => {
              setChoosingAdd(false);
              setActiveTab('ours');
              setModal({ mode: 'create', template: null, initialRegion: selectedRegion !== 'all' && selectedRegion !== GLOBAL_REGION_KEY ? selectedRegion : undefined });
            }}>Create text template</button>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '8px 0' }}>Write a reusable cover letter or other document, then add the values to fill from each submission.</p>
          </div>}
          {canReadAdminForms && canWriteAdminForms && <div>
            <button type="button" style={{ width: '100%', textAlign: 'left', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontWeight: 600, cursor: 'pointer' }} onClick={() => { setChoosingAdd(false); setActiveTab('gov'); }}>Upload official PDF</button>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '8px 0' }}>Upload a government PDF and configure its fillable fields. For forms completed on government websites, use the official workflow links.</p>
          </div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={() => setChoosingAdd(false)} style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer' }}>Cancel</button>
          </div>
        </MovableDialog>
      )}

      {/* Tab bar */}
      <div role="tablist" onKeyDown={tabKeyboard} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {canRead && (
          <button
            type="button"
            role="tab"
            id="form-templates-tab-ours"
            aria-controls="form-templates-tabpanel-ours"
            aria-selected={activeTab === 'ours'} tabIndex={activeTab === 'ours' ? 0 : -1}
            onClick={() => setActiveTab('ours')}
            style={tabBtn(activeTab === 'ours')}
          >
            Tenant templates
          </button>
        )}
        {canReadAdminForms && (
          <button
            type="button"
            role="tab"
            id="form-templates-tab-gov"
            aria-controls="form-templates-tabpanel-gov"
            aria-selected={activeTab === 'gov'} tabIndex={activeTab === 'gov' ? 0 : -1}
            onClick={() => setActiveTab('gov')}
            style={tabBtn(activeTab === 'gov')}
          >
            Official forms and portals
          </button>
        )}
        {canReadAdminForms && (
          <button
            type="button"
            role="tab"
            id="form-templates-tab-requirements"
            aria-controls="form-templates-tabpanel-requirements"
            aria-selected={activeTab === 'requirements'} tabIndex={activeTab === 'requirements' ? 0 : -1}
            onClick={() => setActiveTab('requirements')}
            style={tabBtn(activeTab === 'requirements')}
            data-testid="tab-requirements"
          >
            Requirements
          </button>
        )}
      </div>

      {activeTab === 'gov' && canReadAdminForms && (
        <div
          role="tabpanel"
          id="form-templates-tabpanel-gov"
          aria-labelledby="form-templates-tab-gov"
        >
          <GovernmentFormsTab canWrite={canWriteAdminForms} canDelete={canDeleteAdminForms} />
        </div>
      )}

      {activeTab === 'requirements' && canReadAdminForms && (
        <div
          role="tabpanel"
          id="form-templates-tabpanel-requirements"
          aria-labelledby="form-templates-tab-requirements"
        >
          <SubmissionRequirementsTab canWrite={canWriteAdminForms} canDelete={canDeleteAdminForms} />
        </div>
      )}

      {activeTab === 'ours' && canRead && (
        <div
          role="tabpanel"
          id="form-templates-tabpanel-ours"
          aria-labelledby="form-templates-tab-ours"
        >
      {templates === null && !error && <Spinner caption="Loading templates…" />}
      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
          {error}
          <button
            type="button"
            onClick={reload}
            style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* phase-form-templates-region-sections — the Region control is a
          SECTION SELECTOR (not a row filter): "All" stacks every non-empty
          region section; a region shows only its section; "All regions" shows
          the region-agnostic templates. Value lives in ?region= (shareable,
          refresh-safe). */}
      {templates !== null && templates.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <strong>Region</strong>
            <select
              value={selectedRegion}
              onChange={(e) => selectRegion(e.target.value)}
              aria-label="Region filter"
              data-testid="template-region-filter"
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <option value="all">All</option>
              {REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
              <option value={GLOBAL_REGION_KEY}>All regions</option>
            </select>
          </label>
          {/* phase-form-template-versioning — deleted templates are kept and
              recoverable; this reveals them with a Restore action. */}
          {canDelete && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginLeft: 12 }}>
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                data-testid="template-show-deleted-toggle"
              />
              Show deleted
            </label>
          )}
        </div>
      )}

      {templates !== null && renderOurTemplates()}
        </div>
      )}

      {modal && (
        <TemplateEditModal
          mode={modal.mode}
          template={modal.template}
          initialRegion={modal.initialRegion}
          onClose={() => setModal(null)}
          onSaved={(savedName) => {
            const wasCreate = modal.mode === 'create';
            setModal(null);
            reload();
            showToast({
              type: 'success',
              text: wasCreate
                ? `Created template "${savedName}".`
                : `Saved changes to "${savedName}".`,
            });
          }}
        />
      )}
      {diffSource && templates !== null && (
        <FieldDiffModal
          left={diffSource}
          peers={templates.filter((t) => t.Id !== diffSource.Id)}
          onClose={() => setDiffSource(null)}
        />
      )}
      {provenanceTarget && (
        <TemplateProvenanceModal
          template={provenanceTarget}
          onClose={() => setProvenanceTarget(null)}
        />
      )}
      {previewTarget && (
        <TemplatePreviewModal
          template={previewTarget}
          onClose={() => setPreviewTarget(null)}
        />
      )}

      {historyTarget && (
        <VersionHistoryModal
          template={historyTarget}
          canWrite={canWrite}
          onClose={() => setHistoryTarget(null)}
          onRestored={(name, version) => {
            setHistoryTarget(null);
            reload();
            showToast({ type: 'success', text: `Restored "${name}" to version ${version}.` });
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete template"
          message={deleteDialogMessage(deleteTarget.Name, deleteUsage)}
          confirmLabel={
            deleteUsage !== null && deleteUsage !== 'unknown' && deleteUsage.activeCount > 0
              ? 'Cannot delete'
              : 'Delete'
          }
          variant="destructive"
          // Tracker row 10 — no confirming blind (still checking), and no
          // deleting a template an active submission still generates from.
          confirmDisabled={
            deleteUsage === null || (deleteUsage !== 'unknown' && deleteUsage.activeCount > 0)
          }
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirmed(deleteTarget)}
        />
      )}
      <PageToast toast={toast} onDismiss={clearToast} />
    </section>
  );
}

interface ModalProps {
  readonly mode: 'create' | 'edit';
  readonly template: DocumentTemplate | null;
  readonly initialRegion?: string;
  readonly onClose: () => void;
  readonly onSaved: (savedName: string) => void;
}

function TemplateEditModal({
  mode,
  template,
  initialRegion,
  onClose,
  onSaved,
}: ModalProps): ReactElement {
  const [draft, setDraft] = useState<FormDraft>(() =>
    template ? templateToDraft(template) : { ...EMPTY_DRAFT, Region: initialRegion ?? '' },
  );
  // phase-form-template-versioning — every edit creates a new version, and
  // the reason for the change is REQUIRED (the server 400s a blank note).
  const [changeNote, setChangeNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: 'Name' | 'Description' | 'Region' | 'TargetModule' | 'Body', v: string): void =>
    setDraft((d) => ({ ...d, [k]: v }));

  // phase-small-findings-batch-1 (item 1) — the variables list editor.
  const setVariables = (next: TemplateVariable[]): void =>
    setDraft((d) => ({ ...d, Variables: next }));
  const addVariable = (name = ''): void =>
    setDraft((d) => ({ ...d, Variables: [...d.Variables, { name, required: true }] }));
  const updateVariable = (i: number, patch: Partial<TemplateVariable>): void =>
    setDraft((d) => ({
      ...d,
      Variables: d.Variables.map((v, idx) => (idx === i ? { ...v, ...patch } : v)),
    }));
  const removeVariable = (i: number): void =>
    setDraft((d) => ({ ...d, Variables: d.Variables.filter((_, idx) => idx !== i) }));

  // Both directions, recomputed as the author types — the warning names the
  // offenders and NEVER blocks the save (a mismatch can be deliberate).
  const mismatch = analyzeTemplateVariables(
    draft.Body,
    draft.Variables.map((v) => v.name),
  );
  const warnings = variableWarnings(mismatch);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!draft.Name.trim()) {
      setError('Name is required');
      return;
    }
    if (!draft.TargetModule.trim()) {
      setError('Target module is required (e.g. m1.2, m2.3, m3.2.s.1)');
      return;
    }
    if (!draft.Body.trim()) {
      setError('Body is required');
      return;
    }
    // phase-small-findings-batch-1 (item 1) — no JSON to parse any more. The
    // only hard rule left is the one the SERVER also enforces: every declared
    // variable needs a name. Blank rows are dropped rather than 400'd, since
    // an empty row is just an "Add variable" the author did not fill in.
    const variables: TemplateVariable[] = draft.Variables
      .map((v) => ({ ...v, name: v.name.trim() }))
      .filter((v) => v.name !== '');
    const dupes = variables
      .map((v) => v.name)
      .filter((n, i, arr) => arr.indexOf(n) !== i);
    if (dupes.length > 0) {
      setError(`Duplicate variable name(s): ${[...new Set(dupes)].join(', ')}`);
      return;
    }
    if (mode === 'edit' && changeNote.trim() === '') {
      setError('Describe what changed and why — the note is saved with the new version.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: Partial<DocumentTemplate> = {
        Name: draft.Name,
        Description: draft.Description === '' ? null : draft.Description,
        Region: draft.Region === '' ? null : draft.Region,
        TargetModule: draft.TargetModule,
        Body: draft.Body,
        Variables: variables,
      };
      if (mode === 'create') {
        await v2.documentTemplates.create(payload);
      } else if (template) {
        await v2.documentTemplates.update(template.Id, { ...payload, ChangeNote: changeNote.trim() });
      }
      onSaved(draft.Name);
    } catch (err) {
      setError(formatApiError(err, 'Save failed', FORM_TEMPLATE_ERROR_MESSAGES));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    fontSize: 13,
  };

  // phase-movable-dialogs — shared movable shell (resizable — large text areas).
  return (
    <MovableDialog
      persistKey="formTemplates.editor"
      title={mode === 'create' ? 'New template' : `Edit "${template?.Name}"`}
      ariaLabel={mode === 'create' ? 'New template' : 'Edit template'}
      onClose={onClose}
      closeOnBackdrop
      resizable
      minSize={{ width: 560, height: 440 }}
      width="min(800px, 92vw)"
      maxHeight="88vh"
      zIndex={300}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
        >
          ×
        </button>
      }
    >
      <form onSubmit={handleSubmit}>
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {mode === 'create' ? 'Start with the document wording, choose its submission location, then define any values that should change between submissions.' : 'These changes create a new library version. Existing submission copies remain separate.'}
        </p>


        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg)',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {mode === 'edit' && template && <StarterProposalReview templateId={template.Id} onAdopt={patch => setDraft(current => ({ ...current, ...patch }))} />}
        <Field label="Name *">
          <input
            type="text"
            value={draft.Name}
            onChange={(e) => update('Name', e.target.value)}
            required
            aria-label="Template name"
            style={inputStyle}
          />
        </Field>
        <Field label="Description">
          <input
            type="text"
            value={draft.Description}
            onChange={(e) => update('Description', e.target.value)}
            placeholder="Optional human-readable description"
            aria-label="Template description"
            style={inputStyle}
          />
        </Field>
        <Field label="Region">
          <select
            value={draft.Region}
            onChange={(e) => update('Region', e.target.value)}
            aria-label="Template region"
            data-testid="template-region-select"
            style={inputStyle}
          >
            <option value="">— None (applies to all regions) —</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Submission location *">
          <input
            type="text"
            value={draft.TargetModule}
            onChange={(e) => update('TargetModule', e.target.value)}
            placeholder="m1.2, m2.3, m3.2.s.1, …"
            required
            aria-label="Target module"
            style={inputStyle}
          />
        </Field>
        <Field label="Template wording *">
          <textarea
            value={draft.Body}
            onChange={(e) => update('Body', e.target.value)}
            required
            aria-label="Template body"
            rows={12}
            style={{
              ...inputStyle,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 12,
              resize: 'vertical',
            }}
          />
        </Field>
        {/* phase-small-findings-batch-1 (item 1) — a real add/remove list,
            with both-directions checking against the body. */}
        <Field label="Variables">
          <div data-testid="template-variables-editor">
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
              The values substituted into the body wherever{' '}
              <code>{'{{name}}'}</code> appears. Add one row per placeholder.
            </div>
            {draft.Variables.length === 0 && (
              <div
                data-testid="template-variables-empty"
                style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0 8px' }}
              >
                No variables declared.
              </div>
            )}
            {draft.Variables.map((v, i) => (
              <div
                key={i}
                data-testid={`template-variable-row-${i}`}
                style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}
              >
                <input
                  type="text"
                  value={v.name}
                  onChange={(e) => updateVariable(i, { name: e.target.value })}
                  placeholder="name"
                  aria-label={`Variable ${i + 1} name`}
                  data-testid={`template-variable-name-${i}`}
                  style={{ ...inputStyle, flex: '0 0 34%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
                />
                <input
                  type="text"
                  value={v.description ?? ''}
                  onChange={(e) => updateVariable(i, { description: e.target.value })}
                  placeholder="what it is (optional)"
                  aria-label={`Variable ${i + 1} description`}
                  data-testid={`template-variable-description-${i}`}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}
                >
                  <input
                    type="checkbox"
                    checked={v.required}
                    onChange={(e) => updateVariable(i, { required: e.target.checked })}
                    aria-label={`Variable ${i + 1} required`}
                    data-testid={`template-variable-required-${i}`}
                  />
                  Required
                </label>
                <button
                  type="button"
                  onClick={() => removeVariable(i)}
                  aria-label={`Remove variable ${v.name || i + 1}`}
                  data-testid={`template-variable-remove-${i}`}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    padding: '4px 8px',
                    fontSize: 12,
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addVariable()}
              data-testid="template-variable-add"
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 6,
                cursor: 'pointer',
                padding: '4px 10px',
                fontSize: 12,
              }}
            >
              + Add variable
            </button>

            {/* Both-directions mismatch. A WARNING, never a block — and it
                names the offender so it can be acted on. */}
            {warnings.length > 0 && (
              <div
                role="status"
                data-testid="template-variables-warning"
                style={{
                  marginTop: 10,
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: 'var(--amber-bg, #FFF8E1)',
                  border: '1px solid var(--amber, #E0A800)',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                {warnings.map((w) => (
                  <div key={w} style={{ marginBottom: 4 }}>
                    {w}
                  </div>
                ))}
                {mismatch.undeclared.length > 0 && (
                  <button
                    type="button"
                    data-testid="template-variables-add-missing"
                    onClick={() =>
                      setVariables([
                        ...draft.Variables,
                        ...mismatch.undeclared.map((name) => ({ name, required: true })),
                      ])
                    }
                    style={{
                      marginTop: 4,
                      background: 'none',
                      border: '1px solid var(--amber, #E0A800)',
                      borderRadius: 6,
                      cursor: 'pointer',
                      padding: '3px 8px',
                      fontSize: 12,
                    }}
                  >
                    Add the {mismatch.undeclared.length} missing{' '}
                    {mismatch.undeclared.length === 1 ? 'variable' : 'variables'}
                  </button>
                )}
                <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                  This is a warning only — you can still save.
                </div>
              </div>
            )}
          </div>
        </Field>
        {/* phase-form-template-versioning — saving creates version N+1; the
            note travels with it and is REQUIRED (server enforces too). */}
        {mode === 'edit' && (
          <Field label="What changed, and why? *">
            <textarea
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              required
              aria-label="Change note"
              data-testid="template-change-note-input"
              placeholder="e.g. Updated the sponsor address block per FDA comment"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Create template' : 'Save changes'}
          </button>
        </div>
      </form>
    </MovableDialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactElement;
}): ReactElement {
  return (
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
      <span style={{ display: 'block', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

interface HistoryModalProps {
  readonly template: DocumentTemplate;
  readonly canWrite: boolean;
  readonly onClose: () => void;
  readonly onRestored: (name: string, versionLabel: string) => void;
}

/**
 * phase-form-template-versioning — the version history for one template:
 * every version ever saved (newest first), each with its change note, author
 * and date, the full text expandable, and a Restore action. Restoring never
 * discards anything: it writes the old text as a NEW version on top.
 */
function VersionHistoryModal({
  template,
  canWrite,
  onClose,
  onRestored,
}: HistoryModalProps): ReactElement {
  const [versions, setVersions] = useState<TemplateVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<TemplateVersion | null>(null);
  // Form Templates row 8 — the version shown on the "To" side of Compare.
  const [compareRight, setCompareRight] = useState<string | null>(null);

  useEffect(() => {
    v2.documentTemplates
      .versions(template.Id)
      .then(setVersions)
      .catch((err: unknown) => {
        setError(formatApiError(err, 'Failed to load version history', FORM_TEMPLATE_ERROR_MESSAGES));
        setVersions([]);
      });
  }, [template.Id]);

  const handleRestore = async (v: TemplateVersion): Promise<void> => {
    setConfirmRestore(null);
    setRestoring(v.Id);
    try {
      await v2.documentTemplates.restore(template.Id, v.Id);
      onRestored(template.Name, templateVersionLabel(v.VersionNumber));
    } catch (err) {
      setError(formatApiError(err, 'Restore failed', FORM_TEMPLATE_ERROR_MESSAGES));
      setRestoring(null);
    }
  };

  const latest = versions?.[0]?.VersionNumber ?? null;

  return (
    <MovableDialog
      persistKey="formTemplates.versionHistory"
      title={`Version history — ${template.Name}`}
      ariaLabel="Template version history"
      onClose={onClose}
      closeOnBackdrop
      resizable
      minSize={{ width: 520, height: 360 }}
      width="min(760px, 92vw)"
      maxHeight="84vh"
      zIndex={320}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
        >
          ×
        </button>
      }
    >
      <div data-testid="version-history-modal">
        {error && (
          <div role="alert" style={{ color: 'var(--red)', marginBottom: 12, fontSize: 13 }}>
            {error}
          </div>
        )}
        {/* Row 8 — compare two versions (default: the chosen one vs the one before it). */}
        {versions !== null && compareRight !== null && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong style={{ fontSize: 13 }}>Compare versions</strong>
              <button
                type="button"
                data-testid="version-compare-close"
                onClick={() => setCompareRight(null)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}
              >
                Close compare
              </button>
            </div>
            <VersionDiffPanel key={compareRight} templateId={template.Id} versions={versions} initialRight={compareRight} />
          </div>
        )}
        {versions === null && !error && <Spinner caption="Loading versions…" />}
        {versions !== null && versions.length === 0 && !error && (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>
            No versions recorded yet.
          </div>
        )}
        {versions !== null &&
          versions.map((v) => {
            const isCurrent = v.VersionNumber === latest;
            return (
              <div
                key={v.Id}
                data-testid={`version-row-${v.VersionNumber}`}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '10px 12px',
                  marginBottom: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14 }}>
                    Version {templateVersionLabel(v.VersionNumber)}
                  </strong>
                  {isCurrent && (
                    <span
                      data-testid="version-current-chip"
                      style={{
                        background: '#dcfce7',
                        color: '#166534',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      Current
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {formatDate(v.CreatedAt)}
                    {v.ChangedByUserName ? ` · ${v.ChangedByUserName}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {v.ChangeNote ?? <span style={{ color: 'var(--text-muted)' }}>(no note recorded)</span>}
                </div>
                <details style={{ marginTop: 6 }}>
                  <summary style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
                    Show template text
                  </summary>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: 12,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      background: 'var(--bg-subtle, #f8fafc)',
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      padding: 8,
                      maxHeight: 260,
                      overflow: 'auto',
                    }}
                  >
                    {v.Content}
                  </pre>
                </details>
                <button
                  type="button"
                  onClick={() => setCompareRight(v.Id)}
                  data-testid={`version-compare-btn-${v.VersionNumber}`}
                  title="Compare this version with the one before it"
                  style={{
                    marginTop: 8,
                    marginRight: 8,
                    background: 'none',
                    color: 'var(--text-muted)',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  Compare
                </button>
                {canWrite && !isCurrent && (
                  <button
                    type="button"
                    onClick={() => setConfirmRestore(v)}
                    disabled={restoring !== null}
                    data-testid={`version-restore-btn-${v.VersionNumber}`}
                    style={{
                      marginTop: 8,
                      background: 'none',
                      color: 'var(--accent)',
                      border: '1px solid var(--accent)',
                      borderRadius: 4,
                      padding: '4px 10px',
                      cursor: restoring !== null ? 'wait' : 'pointer',
                      fontSize: 12,
                    }}
                  >
                    {restoring === v.Id ? 'Restoring…' : 'Restore this version'}
                  </button>
                )}
              </div>
            );
          })}
      </div>
      {confirmRestore && (
        <ConfirmModal
          title="Restore version"
          message={`Make version ${templateVersionLabel(confirmRestore.VersionNumber)} the current text of "${template.Name}"? Nothing is lost — the restore is saved as a new version on top of the history.`}
          confirmLabel="Restore"
          onCancel={() => setConfirmRestore(null)}
          onConfirm={() => void handleRestore(confirmRestore)}
        >
          {/* Row 8 — what the restore replaces (current text) vs what it puts back. */}
          {versions !== null && (
            <VersionDiffPanel
              templateId={template.Id}
              versions={versions}
              initialLeft="current"
              initialRight={confirmRestore.Id}
              fromLabel="Replaces"
              toLabel="Restores"
              readOnly
            />
          )}
        </ConfirmModal>
      )}
    </MovableDialog>
  );
}
